import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export enum ROUTES {
  LOGIN = '/login',
  HOME = '/',
  ONBOARDING = '/onboarding',
  PROFILE = '/profile',
  WELCOMEBACK = '/profile/welcome-back',
  KYCSUCCESS = '/kyc-successfully',
  CUSTOMERDATA = '/customer-data',
  BORROWERDATA = '/borrower-data',
  AUTHORIZEPAYMENT = '/authorize-payment',
  VERIFYEMAIL = '/verify-email',
  NOT_FOUND = '/not-found',
}

export enum BackendPaths {
  REGISTER = '/auth/register',
  LOGOUT = '/api/logout',
  REFRESH = '/api/refresh',
  QUESTIONNAIRE = '/api/v2/util/master-data/onboarding-questionnaire',
  PRICING = '/api/v2/util/master-data/supplier-fee',
  TNC = '/api/v2/util/master-data/supplier-terms-and-conditions',
  CUSTOMERTNC = '/api/v2/util/master-data/customer-terms-and-conditions',
  ONBOARDING = 'api/onboarding',
  VERIFYEMAIL = 'api/send/verification/email',
  VERIFYEMAILKYC = 'api/send/kyc/verification/email',
  VERIFYBSB = 'api/bsb-info',
  VERIFYBANKACCOUNT = '/api/validate/account',
  REGISTERATIONSTATUS = 'api/registration/status',
  KYCSINGLE = '/api/onboarding/kyc',
  KYCMULTIPLE = '/api/kyc',
  ACCOUNTING_LOGIN = '/api/v2/accounting/auth/login',
  ACCOUNTING_CALLBACK = '/api/v2/accounting/auth/callback',
  ACCOUNTING_CONNECTION_STATUS = '/api/v2/accounting/connection/status',
  ACCOUNTING_SYNC = '/api/v2/accounting/sync',
  INVOICESYNC = '/api/v2/accounting/sync/invoices',
  CUSTOMERLIST = '/api/customers',
  INVITECUSTOMER = '/api/invite/customer',
  INVOICEDATA = '/api/supplier/invoices',
  CUSTOMERCREDITLIMIT = '/api/customer/credit-limit',
  GENERATEQR = '/api/invoices',
  TRANSACTIONSDATA = '/api/supplier/transactions',
  AUTHORIZEPAYMENT = '/api/customer/invoices',
  BORROWERTRANSACTIONDATA = '/api/customer/transactions',
  BORROWERCREDITBALANCE = '/api/customer/credit-balance',
  SUPPLIERBALANCE = '/api/supplier/balance',
  VERIFYEMAILEXTERNAL = 'api/verify-email',
  BORROWERPENDINGPAYMENTS = '/api/customer/pending-invoices',
  BANKINGLINKTOKEN = '/api/openbanking/link/token',
  BANKINGTOKENEXCHANGE = '/api/openbanking/link/token/exchange',
  COMPANYDOCUMENTUPLOAD = '/api/onboarding/company-documents',
  CREATE_ORG_WITH_EIN = '/api/onboarding/ein',
  LINKED_BANK_ACCOUNTS = '/api/openbanking/accounts',
  SET_PRIMARY_BANK_ACCOUNT = '/api/openbanking/accounts/:bank_account_id/set-primary',
  PAYMENTS_SUMMARY = '/api/v2/accounting/invoices/reconciliation',
}

export enum AuthStates {
  AUTHENTICATED = 'authenticated',
  UNAUTHENTICATED = 'unauthenticated',
  LOADING = 'loading',
}

export enum LoaderStatus {
  LOADING = 'loading',
  SUCCESS = 'success',
  HIDDEN = 'hidden',
}

export const RegexTests = {
  DecimalRegex: /^\d*\.?\d{0,2}$/,
};

export enum StepKeys {
  ONBOARDING = 'onboardingQuestion',
  TNC = 'termsAndCondition',
  PRICING = 'creditLimitOverview',
  UPLOAD_DOCUMENT = 'uploadBusinessDocument',
  ABN = 'abnVerification',
  KYC = 'kycStatus',
  BANKDETAILS = 'bankDetailsVerification',
  LINKBANKACCOUNT = 'openBankingConnection',
  ESTIMATEDLIMIT = 'estimatedCreditLimit',
  APPROVEDLIMIT = 'approvedCreditLimit',
}

export enum DirectorKycStatus {
  PASSED = 'passed',
  FAILED = 'failed',
}

export enum ImagePaths {
  AU_FAVICON = '/favicons/favicon.ico',
  US_FAVICON = '/favicons/bank-favicon.ico',
  LOGO = '/images/logo.png',
  BANKLOGO = '/images/local_bank_logo.png',
  SUCCESS = '/images/success-icon.svg',
  EMAILICON = '/images/email-icon.png',
  SUCCESSICON = '/images/successfull-icon.gif',
  BANKIMAGE = '/images/bank.png',
  EMPTYCUSTOMERS = '/images/empty-customers.png',
  EMPTYINVOICES = '/images/empty-invoices.png',
  AMOUNTDUEICON = '/images/amount-due-icon.png',
  LIMITICON = '/images/limit-icon.png',
  CREDITICON = '/images/credit-icon.png',
  USEDLIMITICON = '/images/used-limit-icon.png',
  NOTFOUNDIMAGE = '/images/404-Error.png',
  EXCLAIM_ICON = '/images/exclaimation.png',
}

export enum KycFileType {
  ID = 'id',
  SELFIE = 'selfie',
}

export enum UserRoles {
  SUPPLIER = 'SUPPLIER_ROLE',
  CUSTOMER = 'CUSTOMER_ROLE',
}

export enum TabsLabel {
  CUSTOMERS = 'Customers',
  INVOICES = 'Invoices',
  TRANSACTIONS = 'Transaction History',
  PAYMENTS = 'Pending Payments',
  STATEMENT = 'Statement',
  PAYMENTS_SUMMARY = 'Payments summary',
}

export enum PriceUnit {
  FLAT = 'flat',
  PERCENT = 'percent',
}

export const CustomerStatusFilters = [
  {
    key: 'invitation_sent',
    value: 'Invitation sent',
  },
  {
    key: 'onboarding_in_progress',
    value: 'Onboarding in process',
  },
  {
    key: 'pending',
    value: 'Pending',
  },
  {
    key: 'joined',
    value: 'Joined',
  },
  {
    key: 'failed',
    value: 'Failed',
  },
];

export const MonthFilters = [
  {
    key: 'all',
    value: 'All',
  },
  {
    key: 'current',
    value: 'This month',
  },
  {
    key: 'previous',
    value: 'Last month',
  },
];

export enum SearchKeys {
  CUSTOMERS = 'Customers',
  INVOICES = 'Invoices',
  TRANSACTIONS = 'Transactions',
  BORROWER_TRANSACTIONS = 'Borrower Transactions',
  BORROWER_INVOICES = 'Borrower Invoices',
  PAYMENTS_SUMMARY = 'Payments Summary',
}

export const envVariables = {
  REGION: process.env.NEXT_PUBLIC_REGION,
  BASEURL: process.env.NEXT_PUBLIC_APP_URL,
  APIURL: process.env.NEXT_PUBLIC_API_URL,
  KEYCLOAK_CLIENT_ID: process.env.KEYCLOAK_CLIENT_ID,
  KEYCLOAK_CLIENT_SECRET: process.env.KEYCLOAK_CLIENT_SECRET,
  KEYCLOAK_ISSUER: process.env.KEYCLOAK_ISSUER,
  AUTH_SECRET: process.env.NEXTAUTH_SECRET,
} as const;

export enum Regions {
  US = 'us',
  AUS = 'aus',
}

export enum Providers {
  PLAID = 'Plaid',
  MASTERCARD = 'Mastercard',
  QUICKBOOKS = 'Quickbooks',
  XERO = 'Xero',
}

export const region = envVariables.REGION as Regions | undefined;
export const regionConfig = {
  isUS: region === Regions.US,
  isAUS: region === Regions.AUS,
  provider: region === Regions.US ? Providers.QUICKBOOKS : Providers.XERO,
  bankProvider: region === Regions.US ? Providers.PLAID : Providers.MASTERCARD,
  requiresBusinessDocumentUpload: region === Regions.US,
  showInvoiceQrAction: region !== Regions.US,
  showTransactionTab: region !== Regions.US,
  branchLabel: region === Regions.US ? 'Routing number' : 'Bank state branch',
  pricingLabel: region === Regions.US ? 'Customer' : 'Borrower',
  businessIdentifierLabel: region === Regions.US ? 'EIN' : 'ABN',
} as const;

export enum EAccountingCallbackError {
  XERO = 'xero_login',
  QUICKBOOKS = 'quickbooks_login',
}
